
import React, { useState, useEffect } from 'react';
import { TableCell, TableRow } from '@/components/ui/table';
import { RefreshCw, Trash2, Video, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatButton } from '@/components/dashboard/shared/ChatButton';
import { Appointment } from '../types/appointmentTypes';
import { AppointmentStatusBadges } from './AppointmentStatusBadges';
import { getFullName } from '@/utils/name-helpers';
import { useTranslation } from 'react-i18next';
import { formatDateForDisplay } from '@/utils/date-helpers';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatInTimeZone } from 'date-fns-tz';
import { ReviewModal } from './ReviewModal';

interface AppointmentTableRowProps {
  appointment: Appointment;
  onRefundRequest: (appointmentId: string) => void;
  onDeleteAppointment: (appointmentId: string) => void;
  canRequestRefund: boolean;
  canDelete: boolean;
  userType: 'client' | 'practitioner';
  unreadCount?: number;
  userTimezone?: string;
}

const AppointmentTableRow: React.FC<AppointmentTableRowProps> = ({
  appointment,
  onRefundRequest,
  onDeleteAppointment,
  canRequestRefund,
  canDelete,
  userType,
  unreadCount = 0,
  userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
}) => {
  const { t, i18n } = useTranslation('dashboard');
  const { user } = useAuth();
  const [googleMeetLink, setGoogleMeetLink] = useState<string | null>(null);
  const [isLoadingMeetLink, setIsLoadingMeetLink] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  
  // Check for Google Meet link if appointment is virtual/online
  useEffect(() => {
    const fetchGoogleMeetLink = async () => {
      if (appointment.format !== 'online' && appointment.format !== 'virtual') return;
      
      setIsLoadingMeetLink(true);
      try {
        const { data, error } = await supabase
          .from('google_calendar_meetings')
          .select('meeting_link')
          .eq('booking_id', appointment.id)
          .maybeSingle();
          
        if (!error && data?.meeting_link) {
          setGoogleMeetLink(data.meeting_link);
        }
      } catch (error) {
        console.error('Error fetching Google Meet link:', error);
      } finally {
        setIsLoadingMeetLink(false);
      }
    };
    
    fetchGoogleMeetLink();
  }, [appointment.id, appointment.format]);
  
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    
    return {
      date: formatDateForDisplay(dateString, i18n.language),
      time: formatInTimeZone(date, userTimezone, 'HH:mm')
    };
  };

  const startDateTime = formatDateTime(appointment.start_time);
  const practitionerName = getFullName(appointment.practitioner);

  // Always show chat - no restrictions
  const canChat = true;

  // Show refund button only within 7 days, not already processed, and not free bookings
  const daysSinceAppointment = Math.ceil(
    (new Date().getTime() - new Date(appointment.start_time).getTime()) / (1000 * 3600 * 24)
  );
  const canRequestRefundAlways = userType === 'client' &&
                                appointment.refund_status !== 'processed' &&
                                appointment.payment_status !== 'Free' &&
                                appointment.price_at_booking > 0 &&
                                daysSinceAppointment <= 7;

  return (
    <TableRow>
      <TableCell>{startDateTime.date}</TableCell>
      <TableCell>{startDateTime.time}</TableCell>
      <TableCell>{practitionerName}</TableCell>
      <TableCell>{appointment.service.service_name}</TableCell>
      <TableCell>
        <AppointmentStatusBadges 
          status={appointment.status}
          refundStatus={appointment.refund_status}
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1 max-w-[280px] sm:max-w-none">
          {/* Chat Button - Always available */}
          <ChatButton
            bookingId={appointment.id}
            className="flex items-center gap-1 text-green-600 border-green-200 hover:bg-green-50 min-w-[44px] sm:min-w-[80px]"
            currentUserType={userType}
            otherParticipantName={practitionerName || 'Other Party'}
            unreadCount={unreadCount}
          />

          {/* Google Meet Button for virtual appointments - only show if NOT completed */}
          {(appointment.format === 'online' || appointment.format === 'virtual') &&
           googleMeetLink &&
           appointment.status !== 'Completed' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(googleMeetLink, '_blank')}
              className="flex items-center gap-1 text-blue-600 border-blue-200 hover:bg-blue-50 min-w-[44px] sm:min-w-[80px]"
              disabled={isLoadingMeetLink}
            >
              <Video className="h-4 w-4" />
              <span className="hidden md:inline">Join Meet</span>
            </Button>
          )}

          {/* Review Button for completed appointments */}
          {appointment.status === 'Completed' && userType === 'client' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsReviewModalOpen(true)}
              className="flex items-center gap-1 text-amber-600 border-amber-200 hover:bg-amber-50 min-w-[44px] sm:min-w-[80px]"
            >
              <Star className="h-4 w-4" />
              <span className="hidden md:inline">Review</span>
            </Button>
          )}

          {/* Refund Request Button - Always available for clients */}
          {canRequestRefundAlways && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRefundRequest(appointment.id)}
              className="flex items-center gap-1 text-orange-600 border-orange-200 hover:bg-orange-50 min-w-[44px] sm:min-w-[80px]"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden md:inline">{t('requestRefund')}</span>
            </Button>
          )}

          {/* Delete Button - Only for eligible appointments */}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDeleteAppointment(appointment.id)}
              className="flex items-center gap-1 text-red-600 border-red-200 hover:bg-red-50 min-w-[44px] sm:min-w-[80px]"
            >
              <Trash2 className="h-4 w-4" />
              <span className="hidden md:inline">Delete</span>
            </Button>
          )}
        </div>
      </TableCell>

      {/* Review Modal */}
      {isReviewModalOpen && user && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          bookingId={appointment.id}
          practitionerId={appointment.practitioner_id}
          practitionerName={practitionerName || 'Practitioner'}
          clientId={user.id}
        />
      )}
    </TableRow>
  );
};

export default AppointmentTableRow;
